import {
  ColumnOrderState,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  RowSelectionState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useState, useMemo, ReactNode, Fragment, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { IAutoMessage } from './AutoTablePart';
import { TRequestFilter } from '.';
import CheckSelectTable from '../common/CheckSelectTable';

import { useSelectingRow } from '../common/CheckSelectStore';
import { useSingleAutoMessage } from './AutoStore';
import { useIsDifferenceStore } from '../common/MessagePromptStore';
import ToggleBox from 'components/Table/util/Togglebox';
import RequestErrorModal from '../common/RequestErrorModal';
import { AlertModal } from 'components/Modal/AlertModal';

import { Stack } from 'components/Stack';
import { Text } from 'components/Typography';
import { Button } from 'components/Button';

import { patchMessageAuto, IRequestPatchMessageAutoParams } from '../api';
import dayjs from 'dayjs';
import { TDragablePickerOptions } from 'components/Popup/DragablePicker';
import TableColumnOrderControlModal from 'components/Modal/TableColumnOrderControlModal';
import { ReactComponent as IcSetting } from 'constants/icon/ic_setting.svg';
import theme from 'constants/theme';

import { IDB_Names } from 'constants/IDB';
import setupIndexedDB, { useIndexedDBStore } from 'hooks/useIndexedDB';

type Tprops = {
  data: IAutoMessage[];
  isFetching: boolean;
  refetchdata: () => void;
  requestFilter: TRequestFilter;
  autoMessageListRefetch: () => void;
};

const setting_AutoTableComponent_columns_content: TDragablePickerOptions = [
  // { key: 'status', name: '활성 상태', isSelected: true },
  // { key: 'receiver', name: '수신인', isSelected: true },
  { key: 'process', name: '진행 구분', isSelected: true },
  { key: 'title', name: '메시지 제목', isSelected: true },
  { key: 'trigger', name: '발송 시간', isSelected: true },
];

const columnHelper = createColumnHelper<IAutoMessage>();

const AutoTableComponent = (props: Tprops) => {
  const { selectRow, setselectRow } = useSelectingRow(); // row 선택에만 색상이 들어가야 한다 + 메시지 컴포넌트에 요청
  const { singleMessage, setSingleMessage } = useSingleAutoMessage(); // 메시지 컴포넌트에 요청
  const { isDifference, setIsDifference } = useIsDifferenceStore(); // 메시지 컴포넌트에 요청

  const [checkedRows, setCheckedRows] = useState<IAutoMessage[]>([]); // 체크박스 선택된 원데이터
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({}); // 0: true, 1: true, 2: true 이런식
  const [snapshopLastToggle, setSnapshopLastToggle] = useState<IAutoMessage | null>(null); // 마지막으로 클릭한 토글 row 데이터
  const [isOpenToggleErrorModal, setIsOpenToggleErrorModal] = useState(false); // 토글 요청 실패시 뜨는 모달
  const [isOpenCancelModal, setIsOpenCancelModal] = useState(false); // 다른 행 클릭 시 계속/취소 모달 띄우기
  const [snapshotLastRowSelect, setSnapshotLastRowSelect] = useState<IAutoMessage | null>(null); // 마지막으로 클릭한 row 데이터(취소 모달에서 사용)

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  // 조회항목 설정 처리 관련
  const [TableColumnOrderControlModalIsOpen, setTableColumnOrderControlModalIsOpen] = useState<boolean>(false);
  const [tableColumnSettingOptions, setTableColumnSettingOptions] = useState<TDragablePickerOptions>(
    setting_AutoTableComponent_columns_content
  );
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>([]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [sorting, setSorting] = useState<SortingState>([]);

  // 조회항목 indexedDB 관련
  const { update, getOneByKey } = useIndexedDBStore(IDB_Names.user.stores[0].name);

  const setColumnOrderInIDB = async (memory: TDragablePickerOptions) => {
    let IDB_CurrentUser = (await getOneByKey('userId', parseInt(window.localStorage.getItem('userId') ?? '-33'))) as {
      [key: string]: any;
    };

    if (!IDB_CurrentUser) {
      return;
    } else {
      // messageManualSendTableColumnsSetting 객체에 memory 객체로 저장
      IDB_CurrentUser.messageAutoTableColumnsSetting = {
        ...IDB_CurrentUser.messageAutoTableColumnsSetting,
        memory,
      };

      update({ ...IDB_CurrentUser });
    }
  };

  const getIDBColumn = async () => {
    const targetUserId = window.localStorage.getItem('userId');
    if (!targetUserId) return;
    else {
      let res = await (getOneByKey('userId', parseInt(targetUserId)) as any);

      const updater = (res?.messageAutoTableColumnsSetting?.memory as TDragablePickerOptions)?.map((d, i) => {
        if (d.name !== setting_AutoTableComponent_columns_content.find(x => x.key === d.key)?.name) {
          return {
            ...d,
            name: setting_AutoTableComponent_columns_content.find(x => x.key === d.key)?.name,
          };
        } else {
          return d;
        }
      });

      console.log(updater, 'updater');

      if (updater) setTableColumnSettingOptions(updater as TDragablePickerOptions);
    }
  };

  useEffect(() => {
    setupIndexedDB({
      databaseName: IDB_Names.user.DB_Name,
      version: IDB_Names.user.version,
      stores: IDB_Names.user.stores,
    })
      .then(() => {
        console.log('successfully set');
      })
      .catch(e => console.error('error / unsupported', e));
  }, []);

  useEffect(() => {
    getIDBColumn();
  }, []);

  const [firstDataTrigger, setFirstDataTrigger] = useState(0);

  const queryClient = useQueryClient();
  const { mutate } = useMutation({
    mutationFn: async (params: IRequestPatchMessageAutoParams) => {
      return patchMessageAuto(params);
    },
    onMutate: async () => {
      // console.log('onMutate');
    },
    onSuccess: async () => {
      // refetch 시키면 기존의 페이지가 첫페이지로 넘어가기 때문에 따로 요청 안함
      // 요청이 성공하였으므로 스넵샷은 null 로 변경처리
      await queryClient.invalidateQueries(['autoMessageList', props.requestFilter]);
    },
    onError: async () => {
      //에러 모달 띄우기 + 기존 데이터 refetch 요청 + 스냅샷은 이전으로 되돌리기
      setIsOpenToggleErrorModal(true);
      await queryClient.invalidateQueries(['autoMessageList', props.requestFilter]);
      if (!!snapshopLastToggle) {
        if (checkedRows.some(item => item.code === snapshopLastToggle.code)) {
          // 같은 코드가 있다면 제거
          setCheckedRows(prev => {
            const filteredRow = prev.filter(row => row.code !== snapshopLastToggle.code);
            return filteredRow;
          });
        } else {
          // 없다면 추가
          setCheckedRows(prev => [...prev, snapshopLastToggle]);
        }
      }

      // await props.autoMessageListRefetch();
    },
  });

  function customSetRowSelection(value: any) {
    // console.log('value(): ', value());
    if (Object.keys(value())[0]) {
      // 체크할 때
      setFirstDataTrigger(prev => prev + 1);
      let orders: Array<any> = [];
      Object.keys(value()).forEach(s => {
        orders = [
          ...orders,
          ...instance
            .getCoreRowModel()
            .flatRows.filter(x => x.id === s)
            .map(x => x.original),
        ];
      });
      orders = orders.filter(y => checkedRows.filter(z => z.code === y.code).length === 0);
      // console.log('orders: 123', orders); // 이게 체크한 원 데이터
      setSnapshopLastToggle(orders[0] ?? null);
      setCheckedRows([...checkedRows, ...orders]);
    } else {
      // 체크 해제 할때
      let orders: Array<any> = checkedRows;
      let selectedList = Object.keys(value(rowSelection));
      let unCheckedItem = instance
        .getSelectedRowModel()
        .flatRows.filter(x => selectedList.filter(d => d === x.id).length === 0);
      // console.log('unCheckedItem: ', unCheckedItem); // 이게 체크 해제한 데이터
      setSnapshopLastToggle(unCheckedItem[0]?.original ?? null);
      unCheckedItem.forEach(s => {
        orders = orders.filter(x => x.code !== s.original?.code);
      });
      setCheckedRows(orders);
    }
  }

  // 초기 데이터 로드시 체크박스 선택된 데이터 처리
  useEffect(() => {
    if (props.data.length > 0) {
      props.data.forEach(x => {
        if (x.status) {
          setCheckedRows(props => [...props, x]);
        }
      });
    }
  }, [props.data]);

  useEffect(() => {
    let t: any = {};
    checkedRows.forEach(x => {
      let filtered = instance.getCoreRowModel().flatRows.filter(d => d.original?.code === x.code);
      // console.log('filtered: ', filtered);
      filtered.length > 0 && (t[`${filtered[0].id}`] = true);
    });
    setRowSelection(t);
    // console.log('setRowSelection t: ', t);
  }, [checkedRows, setRowSelection]);

  // 조회항목 설정 모달에서 컬럼 설정 후 적용
  useEffect(() => {
    let columnVisible: { [key: string]: boolean } = {
      status: true,
      receiver: true,
    };

    tableColumnSettingOptions.forEach(d => {
      d.hidden ? (columnVisible[`${d.key}`] = false) : (columnVisible[`${d.key}`] = d.isSelected);
    });
    setColumnVisibility(columnVisible);
    setColumnOrder(['status', 'receiver'].concat(tableColumnSettingOptions.map(d => `${d.key}`)));
  }, [tableColumnSettingOptions]);

  // code: string; // 메시지코드(id)
  // status: boolean; // 활성상태
  // receiver: 'driver' | 'client'; // 수신인 (고객 | 기사 | 중개사 | 차주)
  // process: string; //진행구분 (시작 | 중간 | 완료 | 1차보고 | 최종보고)
  // title: string; //메시지제목
  // trigger: string; // 발송시점

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'status',
        header: '활성 상태',
        cell: ({ row }) => {
          return (
            <StatusWrapper isRowSelected={selectRow == row.original.code}>
              <ToggleBox
                {...{
                  checked: row.getIsSelected(),
                  indeterminate: row.getIsSomeSelected(),
                  onChange: row.getToggleSelectedHandler(),
                }}
                onClick={e => {
                  mutate({
                    id: row?.original.code,
                    isException: row?.original.disabledSendTime,
                    isUse: !row?.original.status, //기존과 반대로 처리
                    exceptionTime: `${row?.original.sendStartTime}-${row?.original.sendEndTime}`,
                  });
                  e.stopPropagation();
                }}
              />
            </StatusWrapper>
          );
        },
        size: 100,
      }),
      columnHelper.accessor(row => row.receiver, {
        id: 'receiver',
        header: '수신인',
        cell: info => {
          if (info.getValue()) {
            const mapper: { [key in string]: ReactNode } = {
              client: '고객',
              driver: '기사', //
              salesman: '중개사',
              consignor: '화주사',
            };
            return mapper[info.getValue()] ?? info.getValue();
          } else return null;
        },
        footer: info => info.column.id,
        size: 83,
      }),
      columnHelper.accessor(row => row.process, {
        id: 'process',
        header: '진행 구분',
        cell: info => {
          if (info.getValue()) {
            const mapper: { [key in string]: ReactNode } = {
              // 준비중 | 진행중 | 완료 | 보고
              1: <AutoStatusDiv color="#7975FF">준비중</AutoStatusDiv>,
              2: <AutoStatusDiv color="#13C097">진행중</AutoStatusDiv>,
              3: <AutoStatusDiv color="#2E66F6">완료</AutoStatusDiv>,
              4: <AutoStatusDiv color="#8F96A9">보고</AutoStatusDiv>,
            };
            return mapper[info.getValue()] ?? info.getValue();
          } else return null;
        },
        footer: info => info.column.id,
        size: 83,
      }),
      columnHelper.accessor(row => row.title, {
        id: 'title',
        header: '메시지 제목',
        // cell: info => {},
        footer: info => info.column.id,
        size: 232,
      }),
      columnHelper.accessor(row => row.trigger, {
        id: 'trigger',
        header: '발송 시간',
        footer: info => info.column.id,
        size: 516,
      }),
    ],
    [checkedRows, selectRow, firstDataTrigger]
  );

  const onClickTableRow = useCallback(
    (e?: React.MouseEvent<HTMLElement>, row?: { original: IAutoMessage }) => {
      // 1. 클릭시 행 선택
      // 2. 다른 행 클릭 시 모달 띄우기 (모달에서 snapshotLastRowSelect 에 저장)
      // 3. 모달에서 취소 클릭 시 다른 선택한 행 선택 처리
      // 4. 모달에서 확인 클릭 시 해당 행 선택 처리

      if (selectRow.length === 0) {
        // 최초 클릭 시
        setSingleMessage(row?.original ?? null);
        setselectRow(row?.original?.code ?? '');
        // setIsDifference(true); // TODO: 해당부분 삭제처리
      } else {
        if (selectRow == row?.original?.code) {
          // 같은 row 클릭 시
          return;
        } else {
          // 다른 row 클릭 시
          if (!isDifference) {
            // isDifference 가 false 일 때는 다른 row 클릭 시 모달 띄우지 않음
            setSingleMessage(row?.original ?? null);
            setselectRow(row?.original?.code ?? '');
          } else {
            // isDifference 가 true 일 때는 다른 row 클릭 시 모달 띄우기
            if (!snapshotLastRowSelect) {
              // 이전에 선택한 row 데이터가 없을 때 모달 창 띄우기
              setSnapshotLastRowSelect(row?.original ?? null);
              return setIsOpenCancelModal(true); // TODO: 해당부분 삭제처리
            } else {
              // 이전에 선택한 row 데이터가 있을 때 (모달창에서 실행하는 경우)
              setSingleMessage(snapshotLastRowSelect);
              setselectRow(snapshotLastRowSelect?.code ?? '');
              // setIsDifference(true); // TODO: 해당부분 삭제처리
              setSnapshotLastRowSelect(null);
              // 다른 행 클릭 되어 데이터가 변경되었으므로 isDifference 를 false 로 변경
              setIsDifference(false);
            }
          }
        }
      }
    },
    [setSingleMessage, setselectRow, setIsDifference, selectRow, snapshotLastRowSelect, isDifference]
  );

  const onClickModalCancel = () => {
    setIsOpenCancelModal(false);
    onClickTableRow();
  };
  const onClickModalContinue = () => {
    setIsOpenCancelModal(false);
    setSnapshotLastRowSelect(null);
  };

  const instance = useReactTable({
    data: props.data,
    columns,
    state: {
      pagination,
      sorting,
      rowSelection,
      columnVisibility,
      columnOrder,
    },
    enableRowSelection: true, //enable row selection for all rows
    initialState: {},
    onRowSelectionChange: customSetRowSelection,
    onColumnOrderChange: setColumnOrder,
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: setSorting,
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),

    sortDescFirst: true,
  });

  return (
    <Fragment>
      <CheckSelectTable
        table={instance}
        isDataFetching={props?.isFetching}
        tableType="auto"
        onRowClick={(e, row: { original: IAutoMessage }) => {
          onClickTableRow(e, row);
        }}
        selectRowType="code"
        clearBottom
        {...{ pagination, setPagination }}
        sc={[
          <Button
            type="button"
            variant={'tertiary'}
            w={126}
            h={32}
            icon={[IcSetting, { width: 16, height: 16, fill: theme.colors.RG04 }]}
            onClick={() => setTableColumnOrderControlModalIsOpen(true)}
          >
            조회항목 설정
          </Button>,
        ]}
      />
      <TableColumnOrderControlModal
        isOpen={TableColumnOrderControlModalIsOpen}
        setIsOpen={setTableColumnOrderControlModalIsOpen}
        options={tableColumnSettingOptions}
        setOptions={setTableColumnSettingOptions}
        initial={setting_AutoTableComponent_columns_content}
        setColumnOrderInIDB={setColumnOrderInIDB}
      />
      <RequestErrorModal
        isOpen={isOpenToggleErrorModal}
        setIsOpen={setIsOpenToggleErrorModal}
        errorMessage={'주문 상태 요청이 실패했습니다.'}
      />
      <AlertModal
        type="WARNING"
        isOpen={isOpenCancelModal}
        messages={
          <Stack sx={{ gap: '8px' }}>
            <Text styleName="body2" color="RG02">
              작성을 취소할까요?
            </Text>
            <Text styleName="body2" color="RG02">
              작성 중이던 내용은 저장되지 않습니다.
            </Text>
          </Stack>
        }
        buttonGroup={
          <ModalButtonDiv>
            <Button variant="tertiary" h={40} sx={{ width: '100%', marginTop: '40px' }} onClick={onClickModalContinue}>
              <Text styleName="body1" color="RG03">
                계속 작성
              </Text>
            </Button>
            <Button variant="primary" h={40} sx={{ width: '100%', marginTop: '40px' }} onClick={onClickModalCancel}>
              <Text styleName="body1" color="RG00">
                작성 취소
              </Text>
            </Button>
          </ModalButtonDiv>
        }
      />
    </Fragment>
  );
};

export default AutoTableComponent;

const StatusWrapper = styled.div<{ isRowSelected: boolean }>`
  display: flex;
  height: 54px;
  padding: 15px 10px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;

  ${({ theme, isRowSelected }) => ({
    background: isRowSelected ? theme.colors.RG03_1 : theme.colors.RG00,
  })}
`;

const ModalButtonDiv = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  align-self: stretch;
`;

const AutoStatusDiv = styled.div`
  width: 42px;
  height: 24px;
  padding: 2px 0px 2px 0px;
  gap: 10px;
  border-radius: 4px 4px 4px 4px;
  opacity: 0px;
  background-color: ${props => props.color};
  color: #ffff;
  text-align: center;
`;
